/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable jsx-a11y/heading-has-content */
import { useState, KeyboardEvent, FormEvent, useRef, useEffect, useMemo, memo } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { Bot, User, AlertCircle } from "lucide-react";
import { TrashIcon, SparkleIcon } from "@phosphor-icons/react";
import { Avatar, AvatarFallback } from "@libs/zapi-ui/shadcn";
import { AppButton, AppTextarea, Typography, ConfirmationModal } from "@libs/zapi-ui/custom";
import { KeyReturnIcon } from "@libs/zapi-ui/assets";
import { usePresentationChat, PresentationChatMessage } from "../hooks/usePresentationChat";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import styles from "@/zapi-frontend/shared/ui/custom/ChatMessage/ChatMessage.module.css";
import { TParsedContent } from "../model/types";
import { parseMessageContent } from "../lib/parseMessageContent";
import { usePresentation } from "@/zapi-frontend/shared/hooks/usePresentation";

const LoadingIndicator = () => (
  <div className={"flex flex-col space-y-3 p-2 " + styles.animatefadeIn}>
    <div className="flex space-x-1 items-center">
      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
    </div>
  </div>
);

type TPresentationChatMessageComponentProps = {
  message: PresentationChatMessage;
  isLoading?: boolean;
};

const PresentationChatMessageComponent = memo(function PresentationChatMessageComponent(
  props: TPresentationChatMessageComponentProps
) {
  const { message, isLoading } = props;

  const [parsedContent, setParsedContent] = useState<TParsedContent>({ type: "loading", content: "Loading..." });

  useEffect(() => {
    if (message.role === "user") {
      setParsedContent({ type: "answer", content: message.content });
      return;
    }

    if (isLoading) {
      setParsedContent({ type: "loading", content: "Loading..." });
      return;
    }

    const parsed = parseMessageContent(message.content);
    setParsedContent(parsed);
  }, [message, isLoading]);

  if (isLoading) {
    return (
      <div className={`${styles.animatefadeIn} flex items-start gap-2 max-w-full text-base`}>
        <Avatar className="h-8 w-8 bg-primary/10">
          <AvatarFallback>
            <Bot size={16} />
          </AvatarFallback>
        </Avatar>
        <div className="rounded-2xl shadow-sm px-4 py-3 max-w-[75%] bg-gray-50 border border-gray-100">
          <LoadingIndicator />
        </div>
      </div>
    );
  }

  const isUser = message.role === "user";
  const isError = parsedContent.type === "error";

  const messageStyles = isUser
    ? "rounded-2xl shadow-sm px-4 py-3 max-w-[75%] bg-gradient-to-r from-blue-500 to-blue-600 text-white"
    : isError
    ? "rounded-2xl shadow-sm px-4 py-3 max-w-[75%] bg-red-50 border border-red-100"
    : "rounded-2xl shadow-sm px-4 py-3 max-w-[75%] bg-gray-50 border border-gray-100";

  return (
    <div className={`${styles.animatefadeIn} flex items-start gap-2 max-w-full ${isUser ? "flex-row-reverse" : ""}`}>
      <Avatar className="h-8 w-8">
        <AvatarFallback>{isUser ? <User size={16} /> : <Bot size={16} />}</AvatarFallback>
      </Avatar>

      <div className={messageStyles}>
        {isError && (
          <div className="flex items-center gap-1 pb-1 mb-2 border-b border-gray-100">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <span className="text-xs font-medium text-red-500">ERROR</span>
          </div>
        )}

        {isUser ? (
          <div className="text-white whitespace-pre-wrap break-words">
            {message.content || parsedContent.content || ""}
          </div>
        ) : (
          <div className="prose prose-sm max-w-none text-gray-800">
            {parsedContent.content && parsedContent.content.trim() ? (
              <ReactMarkdown
                rehypePlugins={[rehypeRaw]}
                components={{
                  a: ({ ...props }) => (
                    <a {...props} target="_blank" rel="noopener noreferrer" className="underline text-blue-500" />
                  ),
                  p: ({ ...props }) => <p {...props} className="mb-3 last:mb-0" />,
                  ul: ({ ...props }) => <ul {...props} className="list-disc pl-5 mb-3" />,
                  ol: ({ ...props }) => <ol {...props} className="list-decimal pl-5 mb-3" />,
                  li: ({ ...props }) => <li {...props} className="mb-1" />,
                  h1: ({ ...props }) => <h1 {...props} className="text-xl font-bold mb-2" />,
                  h2: ({ ...props }) => <h2 {...props} className="text-lg font-bold mb-2" />,
                  h3: ({ ...props }) => <h3 {...props} className="text-md font-bold mb-2" />
                }}
              >
                {parsedContent.content}
              </ReactMarkdown>
            ) : (
              <div className="whitespace-pre-wrap">{parsedContent.content || ""}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

type TPresentationChatMessageListProps = {
  messages: PresentationChatMessage[];
  loadingMessageId: string | null;
};

function PresentationChatMessageList(props: TPresentationChatMessageListProps) {
  const { messages, loadingMessageId } = props;

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesHash = useMemo(() => messages.map((msg) => msg.id).join("-"), [messages]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timeout);
  }, [messagesHash, loadingMessageId]);

  if (messages.length === 0) {
    return (
      <div className="h-full flex items-top justify-center px-5">
        <Typography variant="body2" color="zinc600">
          If you want to update the outline - just ask the chat.
        </Typography>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto px-4 py-4">
      <div className="space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex items-start ${message.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <PresentationChatMessageComponent message={message} isLoading={message.id === loadingMessageId} />
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}

type TPresentationChatFormProps = {
  isLoading: boolean;
  onSubmit: (message: string) => void;
  onClearChat: () => void;
  onAbortRequest: () => void;
  hasMessages: boolean;
  isSendingRequest: boolean;
  isAbortingRequest: boolean;
};

function PresentationChatForm(props: TPresentationChatFormProps) {
  const { isLoading, onSubmit, onClearChat, onAbortRequest, hasMessages, isSendingRequest, isAbortingRequest } = props;
  const [input, setInput] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const { hasSlides } = usePresentation({ enabled: false });

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value.replace(/\u00a0/g, "\n");
    setInput(value);
  };

  const handleCompositionEnd = () => setTimeout(() => setIsComposing(false), 0);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const nativeEvent = e.nativeEvent as globalThis.KeyboardEvent;
    const composing = isComposing || nativeEvent.isComposing;

    if (e.key === "Enter" && !e.shiftKey && !composing) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const messageText = input.trim();
    setInput("");
    onSubmit(messageText);
  };

  return (
    <div className="p-[8px]">
      <div className="flex flex-col relative">
        <form onSubmit={handleSubmit} className="flex flex-col h-auto relative">
          <div className="flex flex-col gap-2 py-1 relative border border-innoveraZinc-150 rounded-[8px] transition-shadow duration-300 ease bg-white hover:ring-2 hover:ring-innoveraZinc-150 focus-within:ring-2 focus-within:ring-innoveraZinc-150">
            <AppTextarea
              fullWidth
              className="h-[80px] resize-none !border-0 !ring-0"
              placeholder="Ask AI..."
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onCompositionStart={() => setIsComposing(true)}
              onCompositionEnd={handleCompositionEnd}
              disabled={isLoading || !hasSlides}
              rows={5}
            />
            <div className="flex gap-2 items-center justify-end px-2 pb-1">
              {!isLoading && (
                <AppButton
                  className="ml-auto"
                  endAdornment={<KeyReturnIcon />}
                  size="sm"
                  type="submit"
                  disabled={isLoading || !input.trim()}
                  isLoading={isSendingRequest}
                >
                  Send
                </AppButton>
              )}
              {isLoading && !isSendingRequest && (
                <AppButton
                  className="ml-auto"
                  color="red400"
                  size="sm"
                  type="button"
                  onClick={onAbortRequest}
                  isLoading={isAbortingRequest}
                >
                  Cancel
                </AppButton>
              )}
              <AppButton
                title="Clear chat"
                size="iconSm"
                type="button"
                onClick={onClearChat}
                disabled={isLoading || !hasMessages}
                className="transition-all duration-100 bg-innoveraRed-50 text-innoveraRed-400"
                startAdornment={<TrashIcon className="h-4 w-4" />}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export function PresentationChat() {
  const {
    messages,
    hasMessages,
    isLoading,
    isSendingRequest,
    isAbortingRequest,
    loadingMessageId,
    handleSubmit,
    handleAbortRequest,
    handleClearChat
  } = usePresentationChat();

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);

  const openConfirmationDialog = () => {
    setIsConfirmationDialogOpen(true);
  };

  const closeConfirmationDialog = () => {
    setIsConfirmationDialogOpen(false);
  };

  const confirmClearChat = () => {
    handleClearChat();
    setIsConfirmationDialogOpen(false);
  };

  return (
    <div
      data-testid="presentation-chat-container"
      className={cn("h-auto w-full mobile:max-w-[341px] bg-white", "transition-all duration-300")}
    >
      <div data-testid="presentation-chat-header" className="w-full h-14 flex items-center justify-between px-5">
        <div className="w-full flex h-[56px] items-center">
          <Typography variant="label2" color="zinc500" className="inline-flex items-center gap-2">
            <SparkleIcon /> Chat
          </Typography>
        </div>
      </div>

      <div data-testid="presentation-chat-content" className="h-[calc(100vh-127px)]">
        <div className="h-full bg-white flex flex-col border-l border-innoveraZinc-150">
          <div className="flex-1 overflow-hidden">
            <PresentationChatMessageList messages={messages} loadingMessageId={loadingMessageId} />
          </div>

          <PresentationChatForm
            isLoading={isLoading}
            onSubmit={handleSubmit}
            onClearChat={openConfirmationDialog}
            onAbortRequest={handleAbortRequest}
            hasMessages={hasMessages}
            isSendingRequest={isSendingRequest}
            isAbortingRequest={isAbortingRequest}
          />

          <ConfirmationModal
            isOpen={isConfirmationDialogOpen}
            onConfirm={confirmClearChat}
            onClose={closeConfirmationDialog}
            title="Clear chat"
            description="Are you sure you want to clear the chat?"
          />
        </div>
      </div>
    </div>
  );
}
